import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, skipWhile, take, tap } from 'rxjs/operators';
import { AuthenticationStateService } from '@app/core/services/authentication-state.service';
import menu from '@app/core/data/menu';
import countingMenu from '@app/core/data/counting-menu';
import { Router, NavigationEnd } from '@angular/router';
import { MatAccordion } from '@angular/material/expansion';
import { UsersService } from '@app/services/users/users.service';
import { SubSink } from 'subsink';
import { Store } from '@ngrx/store';
import { selectCoreState, selectMeState, selectIsNavCollapsed } from '@app/core/store/core.selectors';
import * as CoreActions from '@app/core/store/core.action';
import { NAV_OVERLAY_QUERIES } from '@app/core/layout/nav-breakpoints';
import { StatisticsService } from '@app/services/statistics/statistics.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnInit, OnDestroy {
  @ViewChild(MatAccordion) matAccordion: MatAccordion;
  public openedPanelKey: string | null = null;
  public countingModule;
  public combinedMenu = menu.menu;
  public assetModule;
  public countingMenu = countingMenu.menu;
  public panelOpenState = false;
  public currentURL: any;
  public baseURL: any;
  public suffix: string;
  public subs: SubSink = new SubSink();
  // Same question the layout asks (see nav-breakpoints.ts): is the sidebar an
  // overlay right now? Only then does picking a page close it.
  public isHandset$: Observable<boolean> = this.breakpointObserver.observe(NAV_OVERLAY_QUERIES)
    .pipe(
      map(result => result.matches),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  public isAsset = null;
  public isCounting = null;
  public isSideNavOpen$: boolean = false;
  public isOverlayNav: boolean = false;
  public settingAccessLimiter;
  public countingMenuAccessLimit;
  public show_location: boolean;

  // Live counts shown as badges next to Products / Counts (design parity).
  public productCount: number | null = null;
  public draftCount: number | null = null;

  // Mini-rail mode (icons only); items show a tooltip and parents expand the bar.
  public collapsed = false;

  // Collapsed-rail flyout: which parent's submenu popover is open (by key), and
  // the connected-overlay positions that open it to the RIGHT of the icon.
  public openFlyoutKey: string | null = null;
  public flyoutPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 10 },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 10 },
  ];

  // Sidebar items grouped into design sections; computed once when the
  // access-filtered menu is built (not a per-change-detection getter).
  public navSections: { label: string; items: any[] }[] = [];

  // Design sections + exact item order. Unmapped keys fall into WORKSPACE so
  // nothing is ever dropped. No Dashboard entry: home is reached via the logo.
  private readonly SECTION_OF: { [key: string]: string } = {
    products: 'INVENTORY', counts: 'INVENTORY', warehouse: 'INVENTORY',
    tasks: 'WORKSPACE', reports: 'WORKSPACE', administration: 'WORKSPACE',
  };
  private readonly SECTION_ORDER = ['INVENTORY', 'WORKSPACE'];
  private readonly ITEM_ORDER = ['products', 'counts', 'warehouse', 'tasks', 'reports', 'administration'];

  constructor(
    private breakpointObserver: BreakpointObserver,
    public authenticationStateService: AuthenticationStateService,
    public router: Router,
    public _users: UsersService,
    public store$: Store,
    private _statistics: StatisticsService
  ) {
    // Live badge counts (Products / Counts) — same source as the dashboard.
    this._statistics.onDashboardStatictics().subscribe((res: any) => {
      this.productCount = res?.product_count ?? null;
      this.draftCount = res?.invetory_draft_count ?? null;
    });

    this.store$.select(selectIsNavCollapsed).subscribe(c => this.collapsed = c);
    this.store$
    .select(selectCoreState)
    .pipe(
      filter((res) => res !== undefined && res !== null), 
      tap((res) => {
        this.show_location = res.show_location;
      })
    ).subscribe();

    
    this.store$.select(selectMeState).pipe(
      skipWhile(res => res == null),
      take(2)
    ).subscribe((me) => {
      this.assetModule = me.locUsesAssetModule;
      this.countingModule = me.locUsesCountingModule;
      this.settingAccessLimiter = me.settingAccessLimiter;

      const objSettingAccess = this.settingAccessLimiter;
      const arraySettingAccess = Object.entries(objSettingAccess);

      for (const itemAccess of arraySettingAccess){
        const keyToRemove = itemAccess[0];
        const accessValue = itemAccess[1]['access'];
        const keySubMenu = Object.entries(itemAccess[1]);

        if(keyToRemove=='warehouse'){
          // Pick/Receive/Pick Lists are children of the Warehouse group; remove
          // the child the user lacks access to (the empty-parent cleanup below
          // hides the whole group when none are left).
          const removeWarehouseChild = (childKey: string) => {
            countingMenu.menu = countingMenu.menu.map(item => {
              if (item.key === 'warehouse' && item.children) {
                return { ...item, children: item.children.filter(child => child.key !== childKey) };
              }
              return item;
            });
          };
          for (const [key, value] of Object.entries(keySubMenu)){
            if(value[0] =='pickings' && value[1]['access']==false){
              removeWarehouseChild('picking');
            }
            if(value[0] =='receivings' && value[1]['access']==false){
              removeWarehouseChild('receiving');
            }
            if(value[0] =='picklists' && value[1]['access']==false){
              removeWarehouseChild('picklist');
            }
            if(value[0] =='storages' && value[1]['access']==false){
              countingMenu.menu = countingMenu.menu.map(item => {
                if (item.key === 'products' && item.children) {
                  return {
                    ...item,
                    children: item.children.filter(child => child.key !== 'storages')
                  };
                }
                return item;
              });
            }
          }
        }

        if(keyToRemove=='administration'){
          for (const [key, value] of Object.entries(keySubMenu)){
            if(value[0] =='reports' && value[1]['access']==false){
              countingMenu.menu = countingMenu.menu.filter(itemMenu => itemMenu.key !== 'reports');
            }
          }
        }

        if(keyToRemove=='integrations' && !accessValue){
          // Integrations is a child of the Administration group; remove that
          // child when the user lacks integrations access.
          countingMenu.menu = countingMenu.menu.map(item => {
            if (item.key === 'administration' && item.children) {
              return { ...item, children: item.children.filter(child => child.key !== 'integrations') };
            }
            return item;
          });
        }

        if (!accessValue) {
          countingMenu.menu = countingMenu.menu.filter(itemMenu => itemMenu.key !== keyToRemove);
        } else{
          
          for (const [key, value] of Object.entries(keySubMenu)){
            if(key != "0"){
              const keySubItem = value[0];
              const keySubItemAccess = value[1]['access'];
              if(!keySubItemAccess){
                countingMenu.menu = countingMenu.menu.map(item => {
                  if (item.key === keyToRemove && item.children) {
                    const updatedChildren = item.children.filter(child => child.key !== keySubItem);
                    return { ...item, children: updatedChildren };
                  }
                  return item;
                });
              }
            }
          }
        }
        
      }

      if(this.show_location==false){
        countingMenu.menu.forEach(menuItem => {
          if (menuItem.children && Array.isArray(menuItem.children)) {
              menuItem.children = menuItem.children.filter(child => child.key !== ""); //child.key !== "locations"
          }
      });
      }

      // Hide the Warehouse group entirely when every child was access-filtered.
      countingMenu.menu = countingMenu.menu.filter(item =>
        item.key !== 'warehouse' || (item.children && item.children.length > 0)
      );

      this.countingMenuAccessLimit = Object.values(countingMenu.menu);
      this.buildNavSections();
      // Update opened panel after menu is loaded
      this.updateOpenedPanelKey();
    });
    
  }

  ngOnInit(): void {
    // Followed for the whole life of the component. It used to stop after the
    // first change (take(2)), so a window that crossed the breakpoint once kept
    // the stale answer.
    this.subs.sink = this.isHandset$.subscribe((isOverlay) => {
      this.isOverlayNav = isOverlay;
    });
    
    this.store$.select(selectCoreState).subscribe((res) => {
      this.isSideNavOpen$ = res.isSideNavOpen;
    });

    this.baseURL = this.router.url.split('/')[1];
    this.suffix = this.router.url.split('/')[this.router.url.split('/').length - 1];
    // Initialize openedPanelKey based on current route (will be set after menu loads)
    this.updateOpenedPanelKey();
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this.baseURL = this.router.url.split('/')[1];
        this.suffix = this.router.url.split('/')[this.router.url.split('/').length - 1];
        // Update openedPanelKey when navigating to a new section
        this.updateOpenedPanelKey();
      }
    });
  }

  private updateOpenedPanelKey(): void {
    // Open the accordion whose key matches the route, or whose children link
    // to it (Warehouse children live under /pickings, /receivings, /picklists).
    if (this.countingMenuAccessLimit) {
      const menuItem = this.countingMenuAccessLimit.find(m =>
        m.children && (
          m.key === this.baseURL ||
          m.children.some(child => child.link === '/' + this.baseURL)
        )
      );
      this.openedPanelKey = menuItem ? menuItem.key : null;
    }
  }

  onCloseNav(): void {

  }

  // Groups the access-filtered menu into the design's sections.
  // Returns [{ label, items }] in section + item order.
  private buildNavSections(): void {
    const items = [...(this.countingMenuAccessLimit || [])];
    const buckets: { [section: string]: any[] } = {};

    for (const item of items) {
      const section = this.SECTION_OF[item.key] || 'WORKSPACE';
      (buckets[section] = buckets[section] || []).push(item);
    }

    const orderOf = (k: string) => {
      const i = this.ITEM_ORDER.indexOf(k);
      return i === -1 ? 999 : i;
    };

    this.navSections = this.SECTION_ORDER
      .filter(s => buckets[s] && buckets[s].length)
      .map(label => ({
        label,
        items: buckets[label].sort((a, b) => orderOf(a.key) - orderOf(b.key)),
      }));
  }

  // Live badge value for a given menu key (Products / Counts only).
  badgeFor(key: string): number | null {
    if (key === 'products') { return this.productCount; }
    if (key === 'counts') { return this.draftCount; }
    return null;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onNavigate(){
    // Close the sidebar after a pick only where it is an overlay; a docked
    // sidebar stays where it is.
    if(this.isOverlayNav == true){
      return this.store$.dispatch(CoreActions.setIsSideNavOpen({ isSideNavOpen: false }));
    }
  }

  // In rail mode, clicking a parent (submenu) item expands the sidebar so its
  // children become visible (the panel toggles open underneath).
  onCollapsedParentClick(){
    if (this.collapsed) {
      this.store$.dispatch(CoreActions.setIsSideNavCollapsed({ isSideNavCollapsed: false }));
    }
  }

  // Collapsed rail: toggle the right-side flyout for the clicked parent.
  toggleFlyout(key: string): void {
    this.openFlyoutKey = this.openFlyoutKey === key ? null : key;
  }

  closeFlyout(): void {
    this.openFlyoutKey = null;
  }

  onPanelOpened(key: string): void {
    this.openedPanelKey = key;
  }

  onPanelClosed(key: string): void {
    // Only clear if the closed panel was the currently open one
    if (this.openedPanelKey === key) {
      this.openedPanelKey = null;
    }
  }

}
